import styled from "styled-components";
import {
  WorkTypeSemanticTone,
  workTypeDesignTokens
} from "./work-type-design-tokens";

export type PolicyKind = "APPROVAL" | "BASE";
export type WorkType = "staggered" | "flexible" | "selective";
export type PolicyStatus = "ACTIVE" | "INACTIVE" | "DELETED";

export interface WorkPolicySummaryModel {
  id: string;
  name: string;
  policyKind: PolicyKind;
  workType: WorkType;
  status: PolicyStatus;
  exposureStatus?: "VISIBLE" | "HIDDEN";
  ruleSummary: string;
}

interface WorkPolicySummaryProps {
  policy: WorkPolicySummaryModel;
  variant: "admin" | "approval" | "snapshot";
  selected?: boolean;
  appearance?: "legacy" | "modern";
}

const policyKindText: Record<PolicyKind, string> = {
  APPROVAL: "결재 신청용",
  BASE: "기본 정책"
};

const workTypeText: Record<WorkType, string> = {
  staggered: "시차",
  flexible: "탄력",
  selective: "선택"
};

const modernWorkTypeText: Record<WorkType, string> = {
  staggered: "시차출퇴근제",
  flexible: "탄력적 근로시간제",
  selective: "선택적 근로시간제"
};

const statusText: Record<PolicyStatus, string> = {
  ACTIVE: "사용 중",
  INACTIVE: "미 사용",
  DELETED: "비활성화됨"
};

const Summary = styled.article<{
  $selected: boolean;
  $appearance: "legacy" | "modern";
}>`
  display: grid;
  gap: 8px;
  min-width: 0;
  padding: 12px;
  border: 1px solid
    ${props =>
      props.$appearance === "modern"
        ? props.$selected
          ? workTypeDesignTokens.color.focus
          : workTypeDesignTokens.color.border
        : props.$selected
        ? props.theme.colors.green
        : props.theme.colors.frameGrey};
  border-left-width: ${props => (props.$selected ? "4px" : "1px")};
  border-radius: ${props =>
    props.$appearance === "modern" ? workTypeDesignTokens.radius.card : "6px"};
  background: white;

  ${props =>
    props.$appearance === "modern" &&
    `
      padding: ${workTypeDesignTokens.spacing.md};
      box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06);
      color: ${workTypeDesignTokens.color.text};
    `}
`;

const Title = styled.h3`
  margin: 0;
  font-size: 14px;
  line-height: 1.45;
  overflow-wrap: anywhere;
`;

const BadgeRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
`;

const Badge = styled.span<{
  $appearance: "legacy" | "modern";
  $tone?: WorkTypeSemanticTone;
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  min-height: 26px;
  border: 1px solid
    ${props =>
      props.$appearance === "modern" && props.$tone
        ? workTypeDesignTokens.tone[props.$tone].border
        : props.theme.colors.grey};
  border-radius: 999px;
  padding: 3px 10px;
  color: ${props =>
    props.$appearance === "modern" && props.$tone
      ? workTypeDesignTokens.tone[props.$tone].text
      : props.theme.colors.midNightBlue};
  background: ${props =>
    props.$appearance === "modern" && props.$tone
      ? workTypeDesignTokens.tone[props.$tone].background
      : props.theme.colors.lightGrey};
  font-size: ${props => (props.$appearance === "modern" ? "12px" : "11px")};
  font-weight: 700;
  line-height: 1;
  white-space: nowrap;
`;

const Rule = styled.p`
  margin: 0;
  color: ${props => props.theme.colors.darkGrey};
  line-height: 1.45;
  overflow-wrap: anywhere;
`;

export const WorkPolicySummary = ({
  policy,
  variant,
  selected = false,
  appearance = "legacy"
}: WorkPolicySummaryProps) => (
  <Summary
    $selected={selected}
    $appearance={appearance}
    aria-label={`${policy.name} 정책 요약`}
    data-variant={variant}
    data-appearance={appearance}
  >
    <Title>{policy.name}</Title>
    <BadgeRow aria-label="정책 상태">
      <Badge
        $appearance={appearance}
        $tone={appearance === "modern" ? policy.policyKind === "APPROVAL" ? "approval" : "active" : undefined}
        data-tone={appearance === "modern" ? policy.policyKind === "APPROVAL" ? "approval" : "active" : undefined}
      >
        {policyKindText[policy.policyKind]}
      </Badge>
      <Badge
        $appearance={appearance}
        $tone={appearance === "modern" ? "neutral" : undefined}
        data-tone={appearance === "modern" ? "neutral" : undefined}
      >
        {appearance === "modern"
          ? modernWorkTypeText[policy.workType]
          : workTypeText[policy.workType]}
      </Badge>
      <Badge
        $appearance={appearance}
        $tone={
          appearance === "modern"
            ? policy.status === "ACTIVE"
              ? "active"
              : "danger"
            : undefined
        }
        data-tone={
          appearance === "modern"
            ? policy.status === "ACTIVE"
              ? "active"
              : "danger"
            : undefined
        }
      >
        {statusText[policy.status]}
      </Badge>
      {policy.exposureStatus && (
        <Badge
          $appearance={appearance}
          $tone={
            appearance === "modern"
              ? policy.exposureStatus === "VISIBLE"
                ? "approval"
                : "danger"
              : undefined
          }
        >
          {appearance === "modern" ? "결재 " : ""}
          {policy.exposureStatus === "VISIBLE" ? "노출" : "숨김"}
        </Badge>
      )}
      {selected && (
        <Badge
          $appearance={appearance}
          $tone={appearance === "modern" ? "selected" : undefined}
        >
          선택됨
        </Badge>
      )}
    </BadgeRow>
    <Rule>{policy.ruleSummary}</Rule>
  </Summary>
);
